/**
 * useJobStatus Hook
 * Updated to match backend API_ROUTES_FINAL.md specification.
 * Custom hook for checking download job status with polling capability.
 * 
 * All variable names, logs, and comments are in English.
 */

import { useApi } from './useApi';
import { statusApi } from '../api';
import { useState, useEffect } from 'react';
import type { StatusResponse } from '../../types/api';

export type JobState = 'pending' | 'downloading' | 'finished' | 'failed';

export interface JobStatus {
  jobId: string;
  state: JobState;
  progress: number;
  title: string;
  fileSize: number;
  format: string;
  quality: string;
  downloadUrl: string;
}

export function useJobStatus(jobId?: string, pollingInterval = 2000) {
  const { loading, error, data, trigger } = useApi<StatusResponse, string>(
    async (id) => {
      return await statusApi.getJobStatus(id);
    }
  );

  const [polling, setPolling] = useState<boolean>(false);

  // Function to check job status
  const checkStatus = async (id: string) => {
    if (!id) return;
    return trigger(id);
  };

  // Start/stop polling
  const startPolling = (id: string) => {
    if (!id) return;
    setPolling(true);
  };

  const stopPolling = () => {
    setPolling(false);
  };

  // Set up polling effect
  useEffect(() => {
    if (!polling || !jobId) return;

    const isCompleted = data?.state === 'finished' || data?.state === 'failed';

    if (isCompleted) {
      stopPolling();
      return;
    }

    const pollTimer = setTimeout(() => {
      checkStatus(jobId);
    }, pollingInterval);

    return () => clearTimeout(pollTimer);
  }, [polling, jobId, data, pollingInterval]);

  // Initial check if jobId is provided
  useEffect(() => {
    if (jobId) {
      checkStatus(jobId);
    }
  }, [jobId]);

  return {
    loading,
    error,
    jobStatus: data ? {
      jobId: data.jobId,
      state: data.state,
      progress: data.progress,
      title: data.title,
      fileSize: data.fileSize,
      format: data.format,
      quality: data.quality,
      downloadUrl: data.downloadUrl
    } : undefined,
    checkStatus,
    startPolling,
    stopPolling,
    isPolling: polling
  };
}

export default useJobStatus;
